import React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import ModalFooterActionContainer from '../TripModal/TripItems/Create/ModalFooterActionContainer'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { ItemMetadata } from 'tripPlanner/models'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { rem } from 'polished'

const StyledModalContent = styled(ModalContent)`
  margin-bottom: ${rem(32)};
`

interface Props {
  item: TripItem
  updateTripDates: () => void
  currentTripDates: string
  extendedTripDates: string
  isLoading: boolean
}

function BookedItemExtendDatesModal(props: Props) {
  const {
    item,
    updateTripDates,
    currentTripDates,
    extendedTripDates,
    isLoading,
  } = props

  const intl = useIntl()
  const { closeModal } = useModalCallbacks()

  const isMobile = useScreenSizeOnly('mobile')
  return (
    <>
      <ModalHeader
        title={intl.formatMessage(
          {
            id: 'I/xE/LwtJa',
            defaultMessage: 'Your booked {itemType} falls outside your trip dates',
            description: 'Modal title when a booked item falls outside the trip date range',
          },
          { itemType: intl.formatMessage(ItemMetadata[item.type].singularName).toLowerCase() },
        )}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <StyledModalContent>
          <VerticalSpacer gap={12}>
            <BodyTextBlock variant="large">
              {intl.formatMessage({
                id: 'ggMJwzErk+',
                defaultMessage: 'Would you like to extend trip duration to include your booking?',
                description: 'Prompt asking user if they want to extend trip dates to fit their booking',
              })}
            </BodyTextBlock>
            <br />
            <div>
              <BodyTextBlock variant="large">
                <b>{intl.formatMessage({
                  id: '24q20O8nCJ',
                  defaultMessage: 'Current trip dates:',
                  description: 'Label for the current trip date range',
                })}
                </b>{' '}{currentTripDates}
              </BodyTextBlock>
              <BodyTextBlock variant="large">
                <b>{intl.formatMessage({
                  id: '9/bIiW7Bqh',
                  defaultMessage: 'New trip dates:',
                  description: 'Label for the proposed new trip date range',
                })}
                </b>{' '}{extendedTripDates}
              </BodyTextBlock>
            </div>
          </VerticalSpacer>
        </StyledModalContent>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActionContainer>
          <TextButton
            kind="secondary"
            size="large"
            fit={`${isMobile ? 'full-width' : 'auto'}`}
            onClick={closeModal}
          >
            {intl.formatMessage({
              id: '3JQoB9bOAO',
              defaultMessage: 'Not for now',
              description: 'Button to dismiss extending trip dates',
            })}
          </TextButton>
          <TextButton
            kind="primary"
            size="large"
            fit={`${isMobile ? 'full-width' : 'auto'}`}
            onClick={updateTripDates}
            disabled={isLoading}
          >
            {intl.formatMessage({
              id: '0WWj9AhCf6',
              defaultMessage: 'Confirm new trip dates',
              description: 'Button to confirm and save the extended trip dates',
            })}
          </TextButton>
        </ModalFooterActionContainer>
      </ModalFooter>
    </>
  )
}

export default BookedItemExtendDatesModal
